import React, { useEffect, useState, useRef, forwardRef } from 'react';
import { Container, Row, Col, Form, Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { CircularProgress, Slider, createTheme, ThemeProvider, IconButton } from '@material-ui/core';
import { CheckboxInput, SelectInput } from '../../../Helpers/Inputs';
import { CollapsibleBasicInfoSummary } from '../../Common/SubComponents/BasicInfoSummary';
import NoQuoteAvailable from '../../Common/SubComponents/NoQuoteAvailable';
import MCTProductDetails from '../../MCT/Helpers/MCTProductDetails';
import GetProductTypeEnum from '../../../../types/productType';
import Tooltip from '@material-ui/core/Tooltip';
import SvgIcon from '@material-ui/core/SvgIcon';
import NumberFormat from 'react-number-format';
import {
  SESSION_STORAGE_ACCOUNT_KEY,
  getSessionStorageItem,
} from '../../../../sessionStorage';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Age from '../../../Helpers/Dates/Age';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import ExpandMoreIcon from '../../../../icons/expand-more.svg';
import CloseIcon from '@material-ui/icons/Close';
import DisabilityInsuranceCard from '../../Common/Cards/DisabilityInsuranceCard';
import DisabilityCoverageAmounts from '../../../Helpers/Lists/CoverageAmounts/DisabilityCoverageAmount';
import ErrorHandler from '../../../Helpers/Errors/ErrorHandler';
import { ConvertBooleanToString } from '../../../Helpers/Formatters/ConvertBoolean';

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

const quoterTheme = {
  typography: {
    fontFamily: "museo-sans",
    body1: {
      fontSize: 14,
      fontWeight: 500,
    },
  },
  overrides: {
    MuiFormControl: {
      root: {
        "&.coverage-amount": {
          display: "flex",
          alignItems: "center",
        },
        "&.coverage-amount .coverage-input": {
          display: "flex",
          width: 188,
          height: 47,
          alignItems: "center",
          borderRadius: 6,

        },
        "&.coverage-amount .coverage-input::before": {
          content: "none",
        },
        "&.coverage-amount .coverage-input::after": {
          content: "none",
        },
        "&.coverage-amount .coverage-input input": {
          padding: 0,
        },
      },
    },

    MuiInputBase: {
      input: {
        fontSize: 16,
      },
      root: {
        height: 56,
      },
    },
    MuiFilledInput: {
      root: {
        backgroundColor: "rgba(0, 0, 0, 0.05)",
      },
      underline: {
        "&::before": {
          borderBottom: "solid 2px #b2b2b2",
        },
        "&:hover::before": {
          borderBottomWidth: "2px",
        },
      },
    },
    MuiFormLabel: {
      root: {
        fontSize: 16,
        color: "rgba(0,0,0,0.6)",
      }
    },
    MuiFormHelperText: {
      root: {
        marginTop: 0,
        paddingTop: 0,
      },
    },
    MuiInputLabel: {
      shrink: {
        color: "rgba(0,0,0,0.3) !important",
      },
    },
    MuiSelect: {
      fontSize: 16,
      icon: {
        color: "rgba(0,0,0,1)"
      },
      iconFilled: {
        right: 16,
      },
    },
    MuiSlider: {
      root: {
        height: 4,
        margin: "8px 0",
        padding: 0,
      },
      rail: {
        height: 4,
        borderRadius: 2,
        backgroundColor: " #ccc",
        opacity: 1,
      },
      track: {
        height: 4,
        borderRadius: 2,
      },
      mark: {
        display: "none",
      },
      thumb: {
        marginTop: -4,
      },
      markLabel: {
        top: 9,
        fontSize: 12,
        color: "#333",
        "&[style*='0%;']": {
          "left": "18px !important",
        },
        "&[style*='100%;']": {
          "right": "-15px !important",
          "left": "auto !important",
        },
      },
    },
    MuiSvgIcon: {
      root: {
        width: 24,
        height: 24,
        marginBottom: 1,
      }
    },
    MuiPaper: {
      outlined: {
        border: "none",
        boxShadow: "0 2px 3px rgba(0, 0, 0, 0.161)",
      },
    },
  },
  MuiAccordionroot: {
    "&.MuiAccordion-root:before": {
      backgroundColor: "#f7f7f7 ",
      height: '0px'
    },
    "&.MuiPaper-elevation1": {
      boxShadow: 'none'
    },
    boxShadow: 'none'
  },
  accordionDetails: {
    padding: '30px 16px 16px',
    display: 'block'
  }
};

export default function Quoter(props) {
  const [loading, setLoading] = useState(false);
  const [quoteRan, setQuoteRan] = useState(false);
  const [quoteOptionsLoading, setQuoteOptionsLoading] = useState(false);
  const [quoteResults, setQuoteResults] = useState([]);
  const [expanded, setExpanded] = useState(!props.hideAllDefault);
  const [show, setShow] = useState(false);
  const [quoteOptions, setQuoteoptions] = useState([]);

  const { register } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const getRiderType = (opt) => {
    let riderType = '';
    switch (opt) {
      case "benefitRider":
        riderType = "BU";
        break;
      case "futureBenefitRider":
        riderType = "FBI";
        break;
      case "residualAndRecoveryRider":
        riderType = "RESR";
        break;
      case "capitalSumBenefitRider":
        riderType = "CSB";
        break;
      case "presumptiveRider":
        riderType = "PDB";
        break;
      case "supplementalHealthRider":
        riderType = "SHB";
        break;
      default:
        riderType = "";
        break;
    }
    return riderType;
  }

  const getRiderValue = () => {

    let ridersValues = [];
    if (props.formState.benefitRider) {
      ridersValues.push({
        riderType: getRiderType("benefitRider"),
      })
    }

    if (props.formState.futureBenefitRider) {
      ridersValues.push({
        riderType: getRiderType("futureBenefitRider"),
      })
    }

    if (props.formState.residualAndRecoveryRider) {
      ridersValues.push({
        riderType: getRiderType("residualAndRecoveryRider"),
      })
    }

    if (props.formState.capitalSumBenefitRider) {
      ridersValues.push({
        riderType: getRiderType("capitalSumBenefitRider"),
      })
    }

    if (props.formState.presumptiveRider) {
      ridersValues.push({
        riderType: getRiderType("presumptiveRider"),
      })
    }

    if (props.formState.supplementalHealthRider) {
      ridersValues.push({
        riderType: getRiderType("supplementalHealthRider"),
      })
    }

    return ridersValues;
  }

  const covrServicesUrl = process.env.REACT_APP_COVR_SERVICES_URL;

  var accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const hideAllDefault = accountObj.accountProducts.find((item) => item.productTypeId === GetProductTypeEnum().FullUW).hideAllProductsDefault;

  useEffect(() => {
    setQuoteOptionsLoading(true);
    const queryParameters = {
      productName: "PrincipalIDI",
      age: Age(props.basicInfo.birthdate),
      gender: props.basicInfo.gender === "M" ? "Male" : "Female",
      isSmoker: props.basicInfo.tobacco === "true" ? true : false,
      isSelfEmployed: props.basicInfo.employment === "Employee" ? false : true,
      benefitAmount: props.formState.coverageAmount || 0,
      occClass: props.fullFormState.pages.employmentDetails.occupationClass,
      annualIncome: Number(props.fullFormState.pages.employmentDetails.annualIncome),
    }

    fetch(`${covrServicesUrl}/api/Breeze/GetQuoteOptions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(queryParameters),
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.isSuccessful) {
          setQuoteOptionsLoading(false)
          setQuoteoptions(json);
          let benefitPeriodOptions = [];
          let eliminationPeriodOptions = [];
          let arrayOfRiders = [];
          json.content.riderOptions.map((value) => {
            arrayOfRiders.push(value.riderType)
          });
          json.content.eliminationPeriods.map((value) => {
            eliminationPeriodOptions.push({ label: `${value} day waiting period`, value: value })
          });
          json.content.benefitPeriods.map((value) => {
            benefitPeriodOptions.push({ label: `${value} Year benefit`, value: value })
          })
          let fullForm = { ...props.fullFormState };
          fullForm.pages.quoter.minBenefitAmount = json.content.minBenefitAmount;
          fullForm.pages.quoter.maxBenefitAmount = json.content.maxBenefitAmount;
          fullForm.pages.quoter.coverageAmount = json.content.maxBenefitAmount * 0.6;
          fullForm.pages.quoter.benefitPeriodOptions = benefitPeriodOptions;
          fullForm.pages.quoter.benefitPeriod = 5 || benefitPeriodOptions[0].value;
          fullForm.pages.quoter.eliminationPeriodOptions = eliminationPeriodOptions;
          fullForm.pages.quoter.waitingPeriod = 90 || eliminationPeriodOptions[0].value;
          fullForm.pages.quoter.arrayOfRiders = arrayOfRiders;
          props.onMultiFieldChange(fullForm);
        }

        if (json && json.isSuccessful) {
          getQuoteList();
        }
      })
  }, [])

  const getQuoteList = () => {
    setQuoteRan(true)
    setLoading(true);
    const quoteData = {
      productName: "PrincipalIDI",
      age: Age(props.basicInfo.birthdate),
      state: props.basicInfo.state,
      gender: props.basicInfo.gender === "M" ? "Male" : "Female",
      occClass: props.fullFormState.pages.employmentDetails.occupationClass,
      discount: props.fullFormState.pages.employmentDetails.discounts,
      isSmoker: props.basicInfo.tobacco = "true" ? true : false,
      isSelfEmployed: true,
      eliminationPeriod: Number(props.formState.waitingPeriod),
      benefitPeriod: Number(props.formState.benefitPeriod),
      riders: getRiderValue(),
    }

    const queryParams = {
      sessionId: accountObj.sessionId,
      oneClickAccountId: accountObj.account.oneClickAccountId,
      quotes: [
        {
          ...quoteData, benefitAmount: parseInt(props.formState.coverageAmount),
        },
        {
          ...quoteData, benefitAmount: parseInt(props.formState.maxBenefitAmount * 0.3),
        },
        {
          ...quoteData, benefitAmount: parseInt(props.formState.maxBenefitAmount * 0.7),
        },
        {
          ...quoteData, benefitAmount: parseInt(props.formState.maxBenefitAmount),
        },
      ]
    };

    fetch(`${covrServicesUrl}/api/Breeze/GetQuoteResults`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(queryParams),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.isSuccessful && data.results.length > 0) {
          setQuoteResults(data.results);
        }
        else {
          setQuoteResults(data);
        }
        setLoading(false);
      })
      .catch((error) => {
        if (error) {
          ErrorHandler(error, setQuoteResults);
          setLoading(false);
        }
      });
  }

  useEffect(() => {

    if (props.formState.coverageAmount == null || props.formState.benefitPeriod == null || props.formState.waitingPeriod === null) {
      return;
    }

    setLoading(true);
    if (quoteOptions && quoteOptions.isSuccessful) {
      getQuoteList()
    }

  }, [quoteOptions, props.formState.coverageAmount, props.formState.benefitPeriod, props.formState.waitingPeriod, props.formState.benefitRider, props.formState.futureBenefitRider, props.formState.residualAndRecoveryRider, props.formState.capitalSumBenefitRider, props.formState.presumptiveRider, props.formState.supplementalHealthRider]);

  const quoteOnClick = (quote) => {
    var form = { ...props.fullFormState };
    form.pages.quoter.coverageAmount = props.formState.coverageAmount;
    form.pages.quoter.benefitPeriod = props.formState.benefitPeriod;
    props.onMultiFieldChange(form);
    props.onNext();
  };

  const handleClick = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleChange = (event, newValue) => {
    props.onFieldChange('coverageAmount', newValue);
  };

  let maxCoverageAmount = Math.floor(props.formState.maxBenefitAmount /100) * 100;

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...quoterTheme,
    })}>
      {quoteOptionsLoading ? <Row className='justify-content-center'><CircularProgress /></Row> :
        <Container className="quoter-wrapper">
          <Row className="quoter">
            <Col lg={4} className="mb-3">
              <Form onSubmit={e => e.preventDefault()}>
                <h2>Customizations Options</h2>
                <h5 className='text-center mt-2'>Benefit Amount</h5>

                <Form.Group className="mb-0 coverage-amount-container">
                  <NumberFormat
                    className="quote-amount-dollars input-coverage-amount"
                    value={props.formState.coverageAmount}
                    displayType={'text'}
                    thousandSeparator={true}
                    decimalScale={0}
                    fixedDecimalScale={true}
                  />
                </Form.Group>

                <Form.Group className="coverage-slider">
                  <Slider
                    min={props.formState.minBenefitAmount}
                    max={maxCoverageAmount}
                    step={null}
                    value={props.formState.coverageAmount}
                    onChange={handleChange}
                    marks={DisabilityCoverageAmounts(props.formState.minBenefitAmount, maxCoverageAmount, 100)}
                    role="slider"
                    aria-valuemin={props.formState.minBenefitAmount}
                    aria-valuenow={props.formState.coverageAmount}
                    aria-valuemax={maxCoverageAmount}
                    aria-labelledby="coverageAmount"
                  />
                </Form.Group>

                <Form.Group className="coverage-terms">
                  <Row>
                    <Col>
                      <SelectInput
                        name="benefitPeriod"
                        id="benefitPeriod"
                        value={props.formState.benefitPeriod}
                        onChange={(e) => props.onFieldChange('benefitPeriod', e.target.value)}
                        label="Benefit Period"
                        hideEmptyOption
                        selectOptions={props.formState.benefitPeriodOptions}
                      />
                    </Col>
                    <Col xs='1' className='pl-0 ml-0 mt-3'>
                      <Tooltip className="toolInfo mt-0 ml-n1" title="The benefit period is the maximum amount of time any combination of total disability or partial disability monthly benefits will be paid while the insured is totally disabled">
                        <LightTooltip >
                          <InfoOutlinedIcon color="disabled"></InfoOutlinedIcon>
                        </LightTooltip>
                      </Tooltip>
                    </Col>
                  </Row>
                </Form.Group>

                <Form.Group className="coverage-terms">
                  <Row>
                    <Col>
                      <SelectInput
                        name="waitingPeriod"
                        id="waitingPeriod"
                        value={props.formState.waitingPeriod}
                        onChange={(e) => props.onFieldChange('waitingPeriod', e.target.value)}
                        label="Waiting Period"
                        hideEmptyOption
                        selectOptions={props.formState.eliminationPeriodOptions}
                      />
                    </Col>
                    <Col xs='1' className='pl-0 ml-0 mt-3'>
                      <Tooltip className="toolInfo mt-0 ml-n1" title="The waiting period (also called an elimination period) is the amount of time you must be continuously, totally disabled before you are eligible to receive monthly benefits.">
                        <LightTooltip >
                          <InfoOutlinedIcon color="disabled"></InfoOutlinedIcon>
                        </LightTooltip>
                      </Tooltip>
                    </Col>
                  </Row>
                </Form.Group>
                <Row>
                  <Col>
                    <Accordion className="filter-accordion pb-0" onChange={() => handleClick('filter-panel')}>
                      <AccordionSummary aria-controls={`filter-content`} id={`filter-header`} onClick={() => setExpanded(!expanded)} className='align-items-center p-0 expand'>
                        Optional Riders &nbsp;<SvgIcon component={ExpandMoreIcon} className={expanded ? 'expanded' : ''} />
                      </AccordionSummary>
                      <AccordionDetails className='row pb-0 pt-0'>
                        <Form.Group className="checkboxes pl-1">
                          <CheckboxInput
                            className="checkBoxInput py-0"
                            name="benefitRider"
                            id="benefitRider"
                            checked={props.formState.benefitRider}
                            onChange={(e) => props.onFieldChange('benefitRider', e.target.checked)}
                            label="Benefit Update"
                            inputRef={register}
                          />

                          <CheckboxInput
                            className="checkBoxInput py-0"
                            name="futureBenefitRider"
                            id="futureBenefitRider"
                            checked={props.formState.futureBenefitRider}
                            onChange={(e) => props.onFieldChange('futureBenefitRider', e.target.checked)}
                            label="Future Benefit Increase"
                            inputRef={register}
                          />

                          <CheckboxInput
                            className="checkBoxInput py-0"
                            name="residualAndRecoveryRider"
                            id="residualAndRecoveryRider"
                            checked={props.formState.residualAndRecoveryRider}
                            onChange={(e) => props.onFieldChange('residualAndRecoveryRider', e.target.checked)}
                            label="Residual & Recovery Benefit"
                            inputRef={register}
                          />

                          <CheckboxInput
                            className="checkBoxInput py-0"
                            name="capitalSumBenefitRider"
                            id="capitalSumBenefitRider"
                            checked={props.formState.capitalSumBenefitRider}
                            onChange={(e) => props.onFieldChange('capitalSumBenefitRider', e.target.checked)}
                            label="Capital Sum Benefit"
                            inputRef={register}
                          />

                          <CheckboxInput
                            className="checkBoxInput py-0"
                            name="presumptiveRider"
                            id="presumptiveRider"
                            checked={props.formState.presumptiveRider}
                            onChange={(e) => props.onFieldChange('presumptiveRider', e.target.checked)}
                            label="Presumptive Disability Benefit"
                            inputRef={register}
                          />

                          <CheckboxInput
                            className="checkBoxInput py-0"
                            name="supplementalHealthRider"
                            id="supplementalHealthRider"
                            checked={props.formState.supplementalHealthRider}
                            onChange={(e) => props.onFieldChange('supplementalHealthRider', e.target.checked)}
                            label="Supplemental Health Benefit"
                            inputRef={register}
                          />
                        </Form.Group>
                      </AccordionDetails>
                    </Accordion>
                  </Col>
                  <Col xs='1' className='pl-0 ml-0 mt-3'>
                    <Tooltip onClick={() => setShow(true)} className="toolInfo mt-0 ml-n1">
                      <LightTooltip >
                        <InfoOutlinedIcon color="disabled"></InfoOutlinedIcon>
                      </LightTooltip>
                    </Tooltip>
                  </Col>
                </Row>

                <CollapsibleBasicInfoSummary
                  gender={props.basicInfo.gender}
                  state={props.basicInfo.state}
                  birthdate={props.basicInfo.birthdate}
                  tobacco={ConvertBooleanToString(props.basicInfo.tobacco)}
                  height={props.basicInfo.height}
                  weight={props.basicInfo.weight}
                  showEditLink={true}
                  editLinkOnClick={props.onBack}
                />
                <div id="sr-message" role="alert" aria-live="assertive" className="sr-only">
                  <h2>{loading ? "loading.." : "product list updated"}</h2>
                </div>
              </Form>
            </Col>

            <Col lg={8} className="result-container">
              <QuoteResult
                formState={props.formState}
                result={quoteResults}
                quoteRan={quoteRan}
                loading={loading}
                onNext={props.onNext}
                quoteOnClick={quoteOnClick}
                hideAllDefault={hideAllDefault}
                coverageAmount={props.formState.coverageAmount}
                term={props.formState.benefitPeriod}
                fullFormState={props.fullFormState}
                onMultiFieldChange={props.onMultiFieldChange}
                quoteOptions={quoteOptions}
                basicInfo={props.basicInfo}
              />
            </Col>
          </Row>

          <Modal animation={false} centered show={show} onHide={() => setShow(false)} size="lg">
            <Modal.Body className="show-grid">
              <Container>
                <Row className="float-right">
                  <IconButton className='p-0' aria-label="close" onClick={() => setShow(false)}>
                    <CloseIcon />
                  </IconButton>
                </Row>
                <Row className='justify-content-center'><h3><b>Optional Riders</b></h3></Row>
                <Row>
                  <ol type='a'>
                    <li>Benefit Update
                      <ol type='1'>
                        <li>Allows insured to purchase increased policy benefits every three years without evidence of medical insurability. Available for standard premium policies if at least 75% of eligible coverage is purchased at issue and is available for all Occupation classes.</li>
                      </ol>
                    </li>
                    <li>Future Benefit Increase (Default Selected)
                      <ol type='1'>
                        <li>Provides an automatic increase in Disability Benefit amount without evidence of insurability, on the six FBI Option Anniversaries following the option date, providing the insured is not disabled. Increase based on the change in the Consumer Price Index for All Urban Consumers (CPI-U); 4% minimum guaranteed increase, 10% maximum (compounded).</li>
                      </ol>
                    </li>
                    <li>Residual & Recovery Benefit
                      <ol type='1'>
                        <li><h6><b>Residual</b></h6>
                          Principal will pay a percentage of the maximum monthly benefit under the disability benefit section and social insurance substitute benefit section. The amount they will pay is equal to the loss of earnings divided by the prior earnings.
                          <div className='mt-3'>
                            <h6><b>Recovery</b></h6>
                            Principal will pay if You are no longer disabled; and You are not receiving benefits under the disability benefit section of the policy; and You return to full-time work immediately after a continuous disability for which benefits were payable under the disability benefit section; and You have a Recovery loss of earnings equal to or greater than 20% of Your Recovery prior earnings.
                          </div></li>
                      </ol>
                    </li>
                    <li>Capital Sum Benefit
                      <ol type='1'>
                        <li>Provides a one-time, lump-sum benefit if an injury or sickness results in a total loss of use for any and every purpose or activity without any possibility of recovery of the use of a hand/foot or the sight of an eye.</li>
                      </ol>
                    </li>
                    <li>Presumptive Disability Benefit
                      <ol type='1'>
                        <li>Provides benefits for total disability under the disability benefit section and social insurance substitute benefit section, regardless of your ability to work or earn an income if you incur as Presumptive Disability. Presumptive Disability is an injury or sickness resulting in total loss of power of speech/hearing in both ears/sight of both eyes/the use of both hands, feet or one hand and one foot, without any possibility of recovery of.</li>
                      </ol>
                    </li>
                    <li>Supplemental Health Benefit
                      <ol type='1'>
                        <li>Provides a one-time, lump-sum benefit, in addition to any other benefit provided by the policy or any other attached riders, that is equal to six times the ultimate Maximum Monthly Disability Benefit and the SIS Benefit, if disabled under the policy and diagnosed with coronary artery bypass graft surgery, cancer or stroke. Available to base benefit periods of 5yrs and greater.</li>
                      </ol>
                    </li>
                  </ol>
                </Row>

              </Container>
            </Modal.Body>
          </Modal>
        </Container>
      }
    </ThemeProvider>
  );
}

function QuoteResult(props) {
  const useStyles = makeStyles((theme) => ({
    textColor: {
      color: theme.palette.primary.main,
    },
    backgroundGradient: {
      "&:hover": {
        background: ` linear-gradient(0deg, ${theme.palette.primary.main}00 0%, ${theme.palette.primary.main}15 100%)`,
      },
      "&.active": {
        background: `linear-gradient(0deg, ${theme.palette.primary.main}15 0%,  ${theme.palette.primary.main}00 100%)`,
      }
    }, backgroundColor: {
      backgroundColor: theme.palette.primary.main,
    },
  }));

  const [quoteCards, setQuoteCards] = useState([]);
  const [highlightedCard, setHighlightedCards] = useState([]);
  const makeQuoteCard = (quote, index) => {
    return {
      component: (
        <DisabilityInsuranceCard
          carrierName={quote.carrierName}
          productName={quote.productName}
          annualRate={quote.annualRate}
          monthlyRate={parseInt(quote.monthlyRate)}
          rating={quote.rating}
          logoUrl={quote.logoUrl}
          benefitAmount={quote.benefitAmount || props.formState.coverageAmount}
          incomeReplacement={quote.incomeReplacement}
          incomeReplacementLabel={quote.incomeReplacementLabel || 'Income Replacement'}
          requiresMedicalExam={quote.requiresMedicalExam}
          key={index}
          onClick={() => {
            var form = { ...props.fullFormState };
            form.pages.quoter.carrierName = quote.carrierName;
            form.pages.quoter.carrierLogoUrl = quote.logoUrl;
            form.pages.quoter.premium = quote.monthlyRate;
            form.pages.quoter.product = quote.product;
            form.pages.quoter.selectedQuote = quote;
            props.onMultiFieldChange(form);
            props.quoteOnClick(quote);
          }}
          productDetails={
            <MCTProductDetails
              carrierName={quote.carrierName}
              productDescription={quote.productDescription}
              hasPermConversion={quote.hasConvertibility}
              productInformation={quote.productInformation}
            />
          }
          availableRiders={quote.availableRiders}
          convertibility={quote.convertibility}
          hasPermConversion={quote.hasConvertibility}
        />
      ),
    };
  };

  const sortCards = () => {
    let fullForm = { ...props.fullFormState };
    if (props.result && props.result.length > 0) {
      let requiresMedicalExam = false;
      let monthlyAmount = Number(fullForm.pages.employmentDetails.annualIncome) / 12;
      let incomeReplacement = ((props.formState.coverageAmount / monthlyAmount) * 100);
      if (Age(props.basicInfo.birthdate) > 50 && props.formState.coverageAmount > 10000) {
        requiresMedicalExam = true;
      }
      var quoteResult = [{ ...props.result[0], incomeReplacement: parseInt(incomeReplacement), requiresMedicalExam: requiresMedicalExam }];
      var newCards = quoteResult.map((r, index) => makeQuoteCard(r, index));
      setQuoteCards([...newCards]);
    }
  };

  useEffect(() => {
    if (!props.result || !props.result.length) {
      return;
    }

    if (props.result && props.result.length > 0) {
      let fullForm = { ...props.fullFormState };
      let quoteResult = { ...props.result };
      let monthlyRateLowerBenefit = Number(fullForm.pages.employmentDetails.annualIncome) / 12;
      const highlightedCardsArray = [];
      const percentageValue = [{ value: 0.3, label: 'Good' }, { value: 0.75, label: 'Better' }, { value: 1, label: 'Best' }];
      for (let i = 0; i < percentageValue.length; i++) {
        let incomeReplacement = (((props.formState.maxBenefitAmount * percentageValue[i].value) / monthlyRateLowerBenefit) * 100);
        let incomeReplacementLabel = `${percentageValue[i].label} Income Replacement`;
        let benefitAmount;
        let requiresMedicalExam = false;
        if (i === 1) {
          benefitAmount = props.formState.maxBenefitAmount * 0.7;
        } else {
          benefitAmount = props.formState.maxBenefitAmount * percentageValue[i].value;
        }
        if (Age(props.basicInfo.birthdate) > 50 && benefitAmount > 10000) {
          requiresMedicalExam = true;
        }

        let lowBenefitAmountCard = { ...quoteResult[i + 1], incomeReplacement: parseInt(incomeReplacement), incomeReplacementLabel: incomeReplacementLabel, benefitAmount: parseInt(benefitAmount), requiresMedicalExam: requiresMedicalExam };
        highlightedCardsArray.push(lowBenefitAmountCard);
      }

      highlightedCardsArray.forEach((item, index) => highlightedCardsArray.push(makeQuoteCard(item, index)));
      setHighlightedCards(highlightedCardsArray);
    }
    sortCards();
  }, [props.result]);

  if (props.loading) {
    return (
      <div className="loading-container">
        <CircularProgress />
      </div>
    );
  }

  if (props.result.isSuccessful === false) {
    return <NoQuoteAvailable subText={props.result.message} />;
  }

  if (!props.result.length) {
    if (props.quoteRan) {
      return <NoQuoteAvailable subText="We’d like to talk to you about your coverage requirement to make sure the product solutions will meet all of your needs. Contact one of our licensed experts to discuss your options." />;
    } else {
      return <span></span>;
    }
  }

  return (
    <div className="quote-result-section">
      <div>
        <h4>Customized Coverage</h4>
      </div>
      <Row className='mt-2 mb-3'>
        <Col>
          <ul className="list-unstyled">
            {quoteCards.map((q, key) => <li key={key} className={key}>{q.component}</li>)}
          </ul>
        </Col>
      </Row>
      <div>
        <h4>More Coverage Options</h4>
      </div>
      <Row className='mt-2'>
        <Col>
          <ul className="list-unstyled">
            {highlightedCard.map((q, key) => <li key={key} className={key}>{q.component}</li>)}
          </ul>
        </Col>
      </Row>

    </div>
  );
}

Quoter.propTypes = {
  formState: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

Quoter.DefaultFormState = (prefill = false) => {
  return {
    coverageAmount: '',
    benefitPeriod: '',
    waitingPeriod: '',
    benefitRider: false,
    futureBenefitRider: true,
    residualAndRecoveryRider: false,
    capitalSumBenefitRider: false,
    presumptiveRider: false,
    supplementalHealthRider: false,
    minBenefitAmount: '',
    maxBenefitAmount: '',
    defaultCoverageAmount: '',
    benefitPeriodOptions: [],
    eliminationPeriodOptions: [],
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/LongTerm/steps/Quoter.js